import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faUser,
  faHeart,
  faBell,
  faPen,
  faUsers,
  faUserLock,
  faBox,
  faCreditCard,
  faLink,
  faCamera,
} from '@fortawesome/free-solid-svg-icons';
import {
  faBarsSort,
  faComments,
  faBuildings,
  faRightFromBracket,
  faArrowUpFromBracket,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { faHammerBrush } from '@fortawesome/pro-duotone-svg-icons';
import { Router, NavigationEnd } from '@angular/router';
import { ProfileService } from '@core/services/profile.service';
import { SidebarService } from '@core/services/side-bar.service';
import { AuthenticationService } from '@core/services/auth.service';
import { ChatStateService } from '@core/services/chat-state.service';
import { NavbarViewManagerService } from '@core/services/navbar-view-manager.service';
import { AgentStateService } from '@core/services/agent-state.service';
import { CollapsibleMenuStateService } from '@core/services/collapsible-menu-state.service';
import { SettingsStateService } from '@core/services/settings-state.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationShellStateService } from '@core/services/navigation-shell-state.service';
import {
  AccessNextStep,
  AccessStateService,
} from '@core/services/access-state.service';
import { SidebarNavigationGroupComponent } from '@shared/sidebar-navigation/sidebar-navigation-group.component';
import { AlertComponent } from '@shared/alert/alert.component';
import {
  PROFILE_ADMIN_GROUP,
  PROFILE_USER_GROUP,
  SETTINGS_ADMIN_GROUP,
  SETTINGS_AGENTS_GROUP,
  SETTINGS_OPERATIONS_GROUP,
  SETTINGS_PRIMARY_GROUP,
  ProfileSidebarAction,
  ProfileSidebarSection,
  SettingsSidebarSection,
  SettingsSidebarAction,
} from '@shared/sidebar-navigation/sidebar-navigation.config';
import {
  SidebarNavItem,
  SidebarNavGroup,
} from '@shared/sidebar-navigation/sidebar-navigation.models';

@Component({
  selector: 'app-sidebar-profile',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    SidebarNavigationGroupComponent,
    AlertComponent,
  ],
  templateUrl: './sidebar-profile.component.html',
  styleUrl: './sidebar-profile.component.css',
})
export class SidebarProfileComponent implements OnInit, OnDestroy {
  readonly settingsPrimaryGroup = SETTINGS_PRIMARY_GROUP;
  readonly settingsAdminGroup = SETTINGS_ADMIN_GROUP;
  readonly settingsOperationsGroup = SETTINGS_OPERATIONS_GROUP;
  readonly settingsAgentsGroup = SETTINGS_AGENTS_GROUP;
  readonly profileAdminGroup = PROFILE_ADMIN_GROUP;
  readonly profileUserGroup = PROFILE_USER_GROUP;

  faUser = faUser;
  faHeart = faHeart;
  faBell = faBell;
  faPen = faPen;
  faBuildings = faBuildings;
  faBarsSort = faBarsSort;
  faUsers = faUsers;
  faUserLock = faUserLock;
  faBox = faBox;
  faCreditCard = faCreditCard;
  faComments = faComments;
  faRightFromBracket = faRightFromBracket;
  faHammerBrush = faHammerBrush;
  faLink = faLink;
  faCamera = faCamera;
  faArrowUpFromBracket = faArrowUpFromBracket;
  faTrash = faTrash;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;

  isCompanyImageDropdownOpen = false;
  isUploadingCompanyImage = false;

  isOpen = true;
  isMobile = false;
  isMenuExpanded = false;
  isProfileSidebarExpanded = true;
  isLogoutInProgress = false;
  accessNextStep: AccessNextStep = 'ready';

  companyName = '';
  companyId = '';
  companyProfileImageUrl: string | undefined = undefined;
  userProfileImageUrl: string | undefined = undefined;

  activeSection:
    | 'personal'
    | 'profile'
    | 'company'
    | 'preferences'
    | 'notifications'
    | 'users'
    | 'payments'
    | 'access'
    | 'products'
    | 'chats' = 'personal';

  // Submenu activo en settings
  activeSettingsSection:
    | 'profile'
    | 'general'
    | 'company'
    | 'users'
    | 'connections'
    | 'payments'
    | 'agents'
    | 'alerts' = 'general';
  showLogoutConfirmation = false;
  showAccessAlert = false;
  accessAlertTitle = '';
  accessAlertMessage = '';
  accessAlertType: 'info' | 'success' | 'warning' | 'alert' = 'warning';
  accessAlertToken = 0;

  userInfo: {
    name: string;
    email: string;
    profile_image_url?: string;
    roles: Array<{
      id: number;
      name: string;
      guard_name: string;
    }>;
    company?: {
      name: string;
      is_company: boolean;
    };
  } = {
    name: '',
    email: '',
    profile_image_url: '',
    roles: [],
    company: undefined,
  };

  private subscriptions = new Subscription();
  private accessAlertTimer: ReturnType<typeof setTimeout> | null = null;

  // Detectar si estamos en modo backoffice basado en la URL actual
  get isBackofficeMode(): boolean {
    const url = this.router.url;

    // Solo es backoffice si la URL contiene explícitamente elementos de backoffice
    const isBackofficeUrl =
      url.includes('/home-backoffice') ||
      (url.includes('/profile') && url.includes('mode=backoffice'));

    // SOLO retornar true si hay evidencia EXPLÍCITA en la URL
    return isBackofficeUrl;
  }

  // Detectar si estamos en settings
  get isSettingsRoute(): boolean {
    return this.router.url.startsWith('/dashboard/settings');
  }

  get isSidebarExpandedView(): boolean {
    return this.isMobile || this.isProfileSidebarExpanded;
  }

  get settingsGroups(): Array<
    SidebarNavGroup<SettingsSidebarAction, SettingsSidebarSection>
  > {
    if (this.isLogoutInProgress) {
      return [];
    }

    const groups: Array<
      SidebarNavGroup<SettingsSidebarAction, SettingsSidebarSection>
    > = [this.settingsPrimaryGroup];

    if (this.isBackofficeMode) {
      return groups;
    }

    const hasAdminRole = this.hasUserRole(1) || this.hasUserRole(2);
    if (hasAdminRole) {
      groups.push(this.settingsAdminGroup);
    }

    groups.push(this.settingsOperationsGroup, this.settingsAgentsGroup);
    return groups;
  }

  get isLicensePendingActivationMode(): boolean {
    return false;
  }

  get settingsFallbackProfileGroup(): SidebarNavGroup<
    ProfileSidebarAction,
    ProfileSidebarSection
  > {
    const profileItem = this.profileAdminGroup.items.find(
      (item) => item.action === 'profile',
    );
    const logoutItem = this.profileUserGroup.items.find(
      (item) => item.action === 'logout',
    );
    const items: SidebarNavItem<
      ProfileSidebarAction,
      ProfileSidebarSection
    >[] = [];

    if (profileItem) {
      items.push(profileItem);
    }

    if (logoutItem) {
      items.push(logoutItem);
    }

    return {
      ...this.profileUserGroup,
      items,
    };
  }

  private filterGroupsByAccess(
    groups: Array<SidebarNavGroup<SettingsSidebarAction, SettingsSidebarSection>>,
  ): Array<SidebarNavGroup<SettingsSidebarAction, SettingsSidebarSection>> {
    return groups;
  }

  private createSettingsGroup(
    id: string,
    actions: SettingsSidebarAction[],
  ): SidebarNavGroup<SettingsSidebarAction, SettingsSidebarSection> {
    const actionSet = new Set(actions);
    const sourceGroups = [
      this.settingsPrimaryGroup,
      this.settingsAdminGroup,
      this.settingsOperationsGroup,
      this.settingsAgentsGroup,
    ];

    const items = sourceGroups
      .flatMap((group) => group.items)
      .filter((item) => actionSet.has(item.action))
      .filter((item) => this.canOpenSettingsAction(item.action));

    return {
      id,
      items,
    };
  }

  private canOpenSettingsAction(action: SettingsSidebarAction): boolean {
    if (this.isLogoutInProgress) {
      return false;
    }

    return true;
  }

  private handleBlockedSettingsAction(action?: SettingsSidebarAction): void {
    this.notifyBlockedSettingsAction(action);
  }

  private canManageAdminSections(): boolean {
    const hasAdminRole = this.hasUserRole(1) || this.hasUserRole(2);
    return hasAdminRole;
  }

  private canManagePaymentsSection(): boolean {
    return true;
  }

  private canNavigateToSettingsSection(action: SettingsSidebarAction): boolean {
    if (!this.canOpenSettingsAction(action)) {
      this.handleBlockedSettingsAction(action);
      return false;
    }

    if (
      (action === 'company' || action === 'users') &&
      !this.canManageAdminSections()
    ) {
      this.handleBlockedSettingsAction(action);
      return false;
    }

    if (action === 'payments' && !this.canManagePaymentsSection()) {
      this.handleBlockedSettingsAction(action);
      return false;
    }

    return true;
  }

  constructor(
    private profileService: ProfileService,
    private sidebarService: SidebarService,
    private authService: AuthenticationService,
    private router: Router,
    private chatStateService: ChatStateService,
    private navbarViewManager: NavbarViewManagerService,
    private agentStateService: AgentStateService,
    private collapsibleMenuStateService: CollapsibleMenuStateService,
    private settingsStateService: SettingsStateService,
    private navigationShellState: NavigationShellStateService,
    private accessStateService: AccessStateService,
  ) {
    this.subscriptions.add(
      this.navigationShellState.sidebarProfileState$.subscribe((state) => {
        this.isOpen = state.isSidebarOpen;
        this.isMobile = state.isMobile;
        this.isMenuExpanded = state.isMenuExpanded;
        this.isProfileSidebarExpanded = state.isProfileSidebarExpanded;
        if (typeof document !== 'undefined') {
          document.body.classList.toggle(
            'profile-sidebar-collapsed',
            !state.isProfileSidebarExpanded,
          );
        }
      }),
    );

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.updateActiveSectionFromRoute(event.url);
        }),
    );
  }

  /** Evita cargar company/user cuando la ruta es solo Knowledge (solo ese componente necesita company). */
  private isKnowledgeRoute(): boolean {
    return this.router.url.includes('/knowledge');
  }

  ngOnInit(): void {
    if (!this.isKnowledgeRoute()) {
      this.loadCompanyProfile();
      this.loadUserProfile();
    }
    this.subscriptions.add(
      this.router.events
        .pipe(filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd))
        .subscribe(() => {
          if (!this.isKnowledgeRoute()) {
            this.loadCompanyProfile();
            this.loadUserProfile();
          }
        }),
    );

    // Initialize active section from URL BEFORE subscribing to service changes
    // This ensures we get the correct state if page is refreshed
    this.updateActiveSectionFromRoute(this.router.url);

    // Now subscribe to service changes
    this.subscriptions.add(
      this.settingsStateService.activeSettingsSection$.subscribe((section) => {
        this.activeSettingsSection = section;
      }),
    );

    this.subscriptions.add(
      this.authService.currentUser$.subscribe((userData) => {
        if (userData) {
          this.userInfo = {
            name: userData.name || '',
            email: userData.email || '',
            profile_image_url: userData.profile_image_url || '',
            roles: userData.roles || [],
            company: userData.company,
          };
        } else {
          this.userInfo = {
            name: '',
            email: '',
            profile_image_url: '',
            roles: [],
            company: undefined,
          };
        }
      }),
    );
    this.subscriptions.add(
      this.authService.logoutInProgress$.subscribe((isLoggingOut) => {
        this.isLogoutInProgress = isLoggingOut;
      }),
    );
    this.subscriptions.add(
      this.accessStateService.accessState$.subscribe((state) => {
        this.accessNextStep = state?.next_step ?? 'ready';
      }),
    );

    this.subscriptions.add(
      this.profileService.showUserProfile$.subscribe((show: boolean) => {
        if (show) {
          this.activeSection = 'personal';
        }
      }),
    );

    this.subscriptions.add(
      this.profileService.showCompanyProfile$.subscribe((show: boolean) => {
        if (show) {
          const currentUrl = this.router.url;
          if (currentUrl.includes('/profile')) {
            this.activeSection = 'profile';
          } else if (currentUrl.includes('/company')) {
            this.activeSection = 'company';
          }
        }
      }),
    );

    this.subscriptions.add(
      this.profileService.companyProfile$.subscribe((companyProfile) => {
        if (companyProfile && companyProfile.company) {
          this.companyName = companyProfile.company.name || '';
          this.companyId = companyProfile.company.nif || '';
          // Solo usar la imagen si la empresa tiene su propia image_path,
          // evita que el backend devuelva la imagen del usuario como fallback
          this.companyProfileImageUrl = companyProfile.company
            .profile_image_path
            ? companyProfile.profile_image_url
            : undefined;
        }
      }),
    );

    this.subscriptions.add(
      this.profileService.userProfile$.subscribe((userProfile) => {
        if (userProfile) {
          this.userProfileImageUrl = userProfile.profile_image_url;
        }
      }),
    );
  }

  toggleProfileSidebar(): void {
    this.collapsibleMenuStateService.toggleProfileSidebarExpanded();
  }

  onImageError(): void {
    this.companyProfileImageUrl = undefined;
  }

  onUserImageError(): void {
    this.userProfileImageUrl = undefined;
  }

  refreshCompanyProfile(): void {
    this.subscriptions.add(
      this.profileService.getCompanyProfile().subscribe({
        next: (response) => {
          if (response && response.company) {
            this.companyName = response.company.name || '';
            this.companyId = response.company.nif || '';
            this.companyProfileImageUrl = response.company.profile_image_path
              ? response.profile_image_url
              : undefined;
          }
        },
        error: (error) => {
          console.error('Error refreshing company profile in sidebar:', error);
        },
      }),
    );
  }

  private loadUserProfile(): void {
    const cachedUserProfile = this.profileService.getCachedUserProfile();
    if (cachedUserProfile) {
      this.userProfileImageUrl = cachedUserProfile.profile_image_url;
      return;
    }

    this.subscriptions.add(
      this.profileService.getUserProfile().subscribe({
        next: (response) => {
          if (response) {
            this.userProfileImageUrl = response.profile_image_url;
          }
        },
        error: (error) => {
          console.error('Error loading user profile in sidebar:', error);
          this.userProfileImageUrl = undefined;
        },
      }),
    );
  }

  private loadCompanyProfile(): void {
    const cachedProfile = this.profileService.getCachedCompanyProfile();

    if (cachedProfile && cachedProfile.company) {
      this.companyName = cachedProfile.company.name || '';
      this.companyId = cachedProfile.company.nif || '';
      this.companyProfileImageUrl = cachedProfile.company.profile_image_path
        ? cachedProfile.profile_image_url
        : undefined;
      return;
    }

    this.subscriptions.add(
      this.profileService.getCompanyProfile().subscribe({
        next: (response) => {
          if (response && response.company) {
            this.companyName = response.company.name || '';
            this.companyId = response.company.nif || '';
            this.companyProfileImageUrl = response.company.profile_image_path
              ? response.profile_image_url
              : undefined;
          }
        },
        error: (error) => {
          console.error('Error loading company profile in sidebar:', error);
          this.companyName = '';
          this.companyId = '';
          this.companyProfileImageUrl = undefined;
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.accessAlertTimer) {
      clearTimeout(this.accessAlertTimer);
      this.accessAlertTimer = null;
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('profile-sidebar-collapsed');
    }
  }

  private notifyBlockedSettingsAction(action?: SettingsSidebarAction): void {
    if (action === 'company' || action === 'users') {
      this.showAccessRestrictionAlert(
        'Acceso restringido',
        'Necesitas perfil supervisor/admin para administrar esta sección.',
      );
      return;
    }

    this.showAccessRestrictionAlert(
      'Módulo no disponible',
      'Tu licencia actual no permite acceder a esta sección.',
    );
  }

  private showAccessRestrictionAlert(title: string, message: string): void {
    if (this.accessAlertTimer) {
      clearTimeout(this.accessAlertTimer);
      this.accessAlertTimer = null;
    }

    this.accessAlertTitle = title;
    this.accessAlertMessage = message;
    this.accessAlertType = 'warning';
    this.accessAlertToken += 1;
    this.showAccessAlert = true;

    this.accessAlertTimer = setTimeout(() => {
      this.showAccessAlert = false;
      this.accessAlertTimer = null;
    }, 4200);
  }

  private updateActiveSectionFromRoute(url: string): void {
    if (url.includes('/profile')) {
      this.activeSection = this.profileService.isCompanyProfileActive()
        ? 'profile'
        : 'personal';
    } else if (url.includes('/company')) {
      this.activeSection = 'company';
    } else if (url.includes('/user-management')) {
      this.activeSection = 'users';
    } else if (url.includes('/payments-invoices')) {
      this.activeSection = 'payments';
    } else if (url.includes('/chat')) {
      this.activeSection = 'chats';
    } else if (url.includes('/settings')) {
      // Try to read a path segment like /dashboard/settings/profile
      let sectionFromUrl: string | null = null;

      const pathMatch = url.match(/\/dashboard\/settings(?:\/([^?\/]+))?/);
      if (pathMatch && pathMatch[1]) {
        sectionFromUrl = pathMatch[1];
      } else {
        // Fallback to query param `section=` for older links
        const match = url.match(/section=([^&]*)/);
        if (match && match[1]) {
          sectionFromUrl = match[1];
        }
      }

      if (sectionFromUrl) {
        // Map possible aliases (e.g. 'billing' -> 'payments')
        let normalized:
          | 'profile'
          | 'general'
          | 'company'
          | 'users'
          | 'connections'
          | 'payments'
          | 'agents'
          | 'alerts'
          | null = null;
        switch (sectionFromUrl) {
          case 'profile':
            normalized = 'profile';
            break;
          case 'general':
            normalized = 'general';
            break;
          case 'company':
            normalized = 'company';
            break;
          case 'users':
            normalized = 'users';
            break;
          case 'payments':
          case 'billing':
            normalized = 'payments';
            break;
          case 'connections':
            normalized = 'connections';
            break;
          case 'agents':
            normalized = 'agents';
            break;
          case 'alerts':
            normalized = 'alerts';
            break;
          default:
            normalized = null;
        }

        if (normalized) {
          this.activeSettingsSection = normalized;
          this.settingsStateService.setActiveSection(normalized);
        }
      } else {
        // If in /settings without a section, default to 'general'
        this.activeSettingsSection = 'general';
        this.settingsStateService.setActiveSection('general');
      }

      return;
    } else {
      this.profileService.hideAllProfiles();
      this.activeSection = 'personal';
    }
  }

  onSettingsNavigationItemClick(action: SettingsSidebarAction): void {
    if (!this.canNavigateToSettingsSection(action)) {
      return;
    }

    switch (action) {
      case 'profile':
        this.onMyProfileClick();
        break;
      case 'general':
        this.onGeneralClick();
        break;
      case 'company':
        this.onMyCompanyClick();
        break;
      case 'users':
        this.onUserManagementClick();
        break;
      case 'connections':
        this.onConnectionsClick();
        break;
      case 'payments':
        this.onPaymentsInvoicesClick();
        break;
      case 'agents':
        this.onAgentsClick();
        break;
      case 'alerts':
        this.onAlertsClick();
        break;
    }
  }

  onProfileNavigationItemClick(action: ProfileSidebarAction): void {
    switch (action) {
      case 'personal':
        this.onPersonalDataClick();
        break;
      case 'profile':
        this.onMyProfileClick();
        break;
      case 'company':
        this.onMyCompanyClick();
        break;
      case 'users':
        this.onUserManagementClick();
        break;
      case 'payments':
        this.onPaymentsInvoicesClick();
        break;
      case 'logout':
        this.logout();
        break;
    }
  }

  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }

  onPersonalDataClick(): void {
    this.activeSection = 'personal';
    this.profileService.showUserProfile();
    this.router.navigate(['/dashboard/profile']);
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onPreferencesClick(): void {
    //this.activeSection = 'preferences';
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onNotificationsClick(): void {
    //this.activeSection = 'notifications';
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  hasUserRole(roleId: number): boolean {
    if (this.userInfo.roles.some((role) => role.id === roleId)) {
      return true;
    }

    const accessUser = this.accessStateService.snapshot?.user;
    if (!accessUser) {
      return false;
    }

    if (roleId === 1 || roleId === 2) {
      return !!accessUser.is_admin || !!accessUser.is_supervisor;
    }

    return false;
  }

  onMyCompanyClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('company')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'company']);
    } else {
      this.activeSection = 'company';
      this.profileService.showCompanyProfile();
      this.router.navigate(['/dashboard/company']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onMyProfileClick(): void {
    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'profile']);
    } else {
      this.activeSection = 'profile';
      this.profileService.showUserProfile();
      this.router.navigate(['/dashboard/profile']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onUserManagementClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('users')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'users']);
    } else {
      this.activeSection = 'users';
      this.profileService.showUserManagement();
      this.router.navigate(['/dashboard/user-management']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onPaymentsInvoicesClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('payments')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'billing']);
    } else {
      this.activeSection = 'payments';
      this.router.navigate(['/dashboard/payments-invoices']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onGeneralClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('general')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'general']);
    } else {
      this.router.navigate(['/dashboard/settings', 'general'], {
        queryParams: { mode: this.isBackofficeMode ? 'backoffice' : null },
      });
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onConnectionsClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('connections')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'connections']);
    } else {
      this.router.navigate(['/dashboard/settings', 'connections']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onAgentsClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('agents')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'agents']);
    } else {
      this.router.navigate(['/dashboard/settings', 'agents']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onAlertsClick(): void {
    if (this.isSettingsRoute && !this.canNavigateToSettingsSection('alerts')) {
      return;
    }

    if (this.isSettingsRoute) {
      this.router.navigate(['/dashboard/settings', 'alerts']);
    } else {
      this.router.navigate(['/dashboard/settings', 'alerts']);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onUserRegistrationClick(): void {
    this.activeSection = 'users';
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onAccessRegistrationClick(): void {
    this.activeSection = 'access';
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onMyProductsClick(): void {
    this.activeSection = 'products';
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  onMyChatsClick(): void {
    this.activeSection = 'chats';
    this.chatStateService.startNewChat(
      this.agentStateService,
      this.navbarViewManager,
    );
    this.navbarViewManager.handleSidebarForChat();
    this.router.navigate(['/dashboard/chat']);
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  logout() {
    this.showLogoutConfirmation = true;
  }

  private performLogout() {
    this.authService.logout().subscribe({
      next: () => {
        //this.router.navigate(['/']);
        window.location.href = '/';
      },
      error: (error) => {
        console.error('Error al cerrar sesión:', error);
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
        this.chatStateService.setPersistedChatId(null);
        localStorage.removeItem('agent_id');
        localStorage.removeItem('type_agent');
        localStorage.removeItem('active_menu_section');
        //this.router.navigate(['/']);
        window.location.href = '/';
      },
    });
  }

  confirmLogout() {
    this.showLogoutConfirmation = false;
    this.performLogout();
  }

  cancelLogout() {
    this.showLogoutConfirmation = false;
  }

  // ── Company Image Management ──

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const dropdownContainer = target.closest(
      '.company-image-dropdown-container',
    );
    if (!dropdownContainer) {
      this.isCompanyImageDropdownOpen = false;
    }
  }

  /** Abre/cierra el dropdown de opciones de imagen de empresa */
  toggleCompanyImageDropdown(event: Event): void {
    event.stopPropagation();
    this.isCompanyImageDropdownOpen = !this.isCompanyImageDropdownOpen;
  }

  /** Abre el selector de archivo para subir imagen de empresa */
  onUploadCompanyImageClick(): void {
    this.isCompanyImageDropdownOpen = false;
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (event) => this.onCompanyImageSelected(event);
    fileInput.click();
  }

  /** Procesa el archivo seleccionado y sube la imagen de empresa */
  private onCompanyImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      console.error('Archivo no es una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      console.error('La imagen debe ser menor a 5MB');
      return;
    }

    this.isUploadingCompanyImage = true;

    this.subscriptions.add(
      this.profileService.updateCompanyProfileImage(file).subscribe({
        next: (response) => {
          console.log('Imagen de empresa actualizada');
          this.isUploadingCompanyImage = false;

          // Actualizar la vista local con FileReader para feedback inmediato
          const reader = new FileReader();
          reader.onload = (e) => {
            this.companyProfileImageUrl = e.target?.result as string;
          };
          reader.readAsDataURL(file);

          // Si el backend devuelve la URL, usarla
          if (response?.profile_image_url) {
            this.companyProfileImageUrl = response.profile_image_url;
          }
        },
        error: (error) => {
          console.error('Error al subir imagen de empresa:', error);
          this.isUploadingCompanyImage = false;
        },
      }),
    );
  }

  /** Elimina la imagen de empresa */
  onDeleteCompanyImageClick(): void {
    this.isCompanyImageDropdownOpen = false;

    this.subscriptions.add(
      this.profileService.deleteCompanyProfileImage().subscribe({
        next: () => {
          console.log('Imagen de empresa eliminada');
          this.companyProfileImageUrl = undefined;
        },
        error: (error) => {
          console.error('Error al eliminar imagen de empresa:', error);
        },
      }),
    );
  }
}
